import { zodResolver } from '@hookform/resolvers/zod';
import { useDisclosure } from '@nextui-org/react';
import { useContext, useMemo, useState } from 'react';
import {
  Control,
  FieldErrors,
  useForm,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

import {
  Theme,
  ThemeContext,
} from '../../../contexts/ThemeContext/ThemeContext';
import { IParceiro } from '../../../interfaces/response/Parceiros/IParceiro';
import { TipoParceiro } from '../../../pages/Parceiros/hooks/useParceiros';
import {
  addParceiroColeta,
  addParceiroEntrega,
  deleteParceiroColeta,
  deleteParceiroEntrega,
  editParceiroColeta,
  editParceiroEntrega,
  getParceirosColetaById,
  getParceirosEntregaById,
} from '../../../redux/features/parceiros/parceirosThunk';
import { useAsyncDispatch } from '../../../redux/store';

const normalizeCep = (cep: string): string => (cep ?? '').replace(/\D/g, '');

const cepMask = (value: string): string => {
  const digits = normalizeCep(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formSchema = z
  .object({
    sigla: z.string().min(1, 'Sigla é obrigatória'),
    cepInicio: z
      .string()
      .min(1, 'CEP início é obrigatório')
      .refine((v) => normalizeCep(v).length === 8, 'CEP início inválido'),
    cepFim: z
      .string()
      .min(1, 'CEP fim é obrigatório')
      .refine((v) => normalizeCep(v).length === 8, 'CEP fim inválido'),
  })
  .refine(
    (data) => {
      const a = Number(normalizeCep(data.cepInicio));
      const b = Number(normalizeCep(data.cepFim));
      return a <= b;
    },
    {
      message: 'CEP fim deve ser maior ou igual ao CEP início',
      path: ['cepFim'],
    }
  );

type FormSchema = z.infer<typeof formSchema>;

interface UseRegisterParceiroModalProps {
  onUpdate?: () => void;
  tipo: TipoParceiro;
}

export interface UseRegisterParceiroModal {
  errors: FieldErrors<FormSchema>;
  handleSubmitParceiro: (data: FormSchema) => Promise<void>;
  handleDeleteParceiro: () => Promise<void>;
  handleClose: () => void;
  handleSubmit: UseFormHandleSubmit<FormSchema>;
  handleOpenEdit: (parceiroAux: IParceiro) => void;
  isLoading: boolean;
  isDeleting: boolean;
  isOpen: boolean;
  isValid: boolean;
  onOpen: () => void;
  onOpenChange: () => void;
  register: UseFormRegister<FormSchema>;
  theme: Theme;
  control: Control<FormSchema>;
  setValue: UseFormSetValue<FormSchema>;
  values: FormSchema;
  parceiro: IParceiro | null;
}

const useRegisterParceiroModal = ({
  onUpdate,
  tipo,
}: UseRegisterParceiroModalProps): UseRegisterParceiroModal => {
  const dispatch = useAsyncDispatch();
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();
  const { theme } = useContext(ThemeContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [parceiro, setParceiro] = useState<IParceiro | null>(null);

  const defaultValues: FormSchema = useMemo(
    () => ({
      sigla: '',
      cepInicio: '',
      cepFim: '',
    }),
    []
  );

  const {
    register,
    handleSubmit,
    clearErrors,
    formState,
    reset,
    control,
    setValue,
    watch,
  } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resetOptions: {
      keepDefaultValues: true,
    },
  });

  const { errors, isValid } = formState;
  const values = watch();

  const handleClose = (): void => {
    onClose();
    setParceiro(null);
    clearErrors();
    reset();
  };

  const getByIdThunk =
    tipo === 'entrega' ? getParceirosEntregaById : getParceirosColetaById;
  const addThunk = tipo === 'entrega' ? addParceiroEntrega : addParceiroColeta;
  const editThunk =
    tipo === 'entrega' ? editParceiroEntrega : editParceiroColeta;
  const deleteThunk =
    tipo === 'entrega' ? deleteParceiroEntrega : deleteParceiroColeta;

  const handleCreate = async (data: FormSchema): Promise<void> => {
    const body: IParceiro = {
      sigla: data.sigla.toUpperCase(),
      cepInicio: normalizeCep(data.cepInicio),
      cepFim: normalizeCep(data.cepFim),
    };

    setIsLoading(true);
    await dispatch(addThunk({ body }))
      .unwrap()
      .then((ok: boolean) => {
        if (!ok) {
          toast.error('Não foi possível cadastrar o parceiro.');
          return;
        }
        toast.success('Parceiro cadastrado com sucesso!');
        handleClose();
        if (onUpdate) onUpdate();
      })
      .catch(() => {
        toast.error(
          'Ocorreu um erro ao realizar o cadastro do parceiro, por favor tente mais tarde.'
        );
      })
      .finally(() => setIsLoading(false));
  };

  const handleEdit = async (data: FormSchema): Promise<void> => {
    if (!parceiro) return;

    const body: IParceiro = {
      id: parceiro.id,
      sigla: data.sigla.toUpperCase(),
      cepInicio: normalizeCep(data.cepInicio),
      cepFim: normalizeCep(data.cepFim),
    };

    setIsLoading(true);
    await dispatch(editThunk({ body }))
      .unwrap()
      .then((ok: boolean) => {
        if (!ok) {
          toast.error('Não foi possível alterar o parceiro.');
          return;
        }
        toast.success('Parceiro editado com sucesso!');
        handleClose();
        if (onUpdate) onUpdate();
      })
      .catch(() => {
        toast.error(
          'Ocorreu um erro ao realizar a alteração do parceiro, por favor tente mais tarde.'
        );
      })
      .finally(() => setIsLoading(false));
  };

  const handleSubmitParceiro = async (data: FormSchema): Promise<void> => {
    if (parceiro) await handleEdit(data);
    else await handleCreate(data);
  };

  const handleDeleteParceiro = async (): Promise<void> => {
    if (!parceiro) return;

    // confirmação simples (mantém o padrão leve da tela)
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir o parceiro ${parceiro.sigla.toUpperCase()}?`
    );
    if (!confirmed) return;

    setIsDeleting(true);
    await dispatch(deleteThunk({ id: parceiro.id ?? '' }))
      .unwrap()
      .then((ok: boolean) => {
        if (!ok) {
          toast.error('Não foi possível excluir o parceiro.');
          return;
        }
        toast.success('Parceiro excluído com sucesso!');
        handleClose();
        if (onUpdate) onUpdate();
      })
      .catch(() => {
        toast.error(
          'Ocorreu um erro ao excluir o parceiro, por favor tente mais tarde.'
        );
      })
      .finally(() => setIsDeleting(false));
  };

  const handleOpenEdit = (parceiroAux: IParceiro): void => {
    // abre modal imediatamente (UX) e carrega o registro atualizado via getById
    setParceiro(parceiroAux);
    setValue('sigla', (parceiroAux.sigla ?? '').toUpperCase());
    setValue('cepInicio', cepMask(parceiroAux.cepInicio ?? ''));
    setValue('cepFim', cepMask(parceiroAux.cepFim ?? ''));
    onOpen();

    setIsLoading(true);
    dispatch(getByIdThunk({ id: parceiroAux.id ?? '' }))
      .unwrap()
      .then((fresh: IParceiro) => {
        setParceiro(fresh);
        setValue('sigla', (fresh.sigla ?? '').toUpperCase());
        setValue('cepInicio', cepMask(fresh.cepInicio ?? ''));
        setValue('cepFim', cepMask(fresh.cepFim ?? ''));
      })
      .catch(() => {
        // se falhar, mantém dados que já estavam na tabela
        toast.error('Erro ao consultar detalhes do parceiro.');
      })
      .finally(() => setIsLoading(false));
  };

  return {
    errors,
    handleSubmitParceiro,
    handleDeleteParceiro,
    handleClose,
    handleSubmit,
    handleOpenEdit,
    isLoading,
    isDeleting,
    isOpen,
    isValid,
    onOpen,
    onOpenChange,
    register,
    theme,
    control,
    setValue,
    values,
    parceiro,
  };
};

export default useRegisterParceiroModal;
